<template>
  <div class="prev-commission-data">
    <div class="table-responsive">
      <table class="table previous-commissions-table mb-0">
        <thead>
          <tr class="bg-white">
            <th scope="col" class="text-center">No</th>
            <th scope="col" class="text-center">Type</th>
            <th scope="col">Student</th>
            <th scope="col">Course</th>
            <th scope="col">Tuition</th>
            <th scope="col">Commissions</th>
            <th scope="col" class="text-nowrap">Student Qty</th>
            <th scope="col" class="text-nowrap">KeyP/Level</th>
            <th scope="col">Referer/Hunter</th>
            <th scope="col">Total</th>
          </tr>
        </thead>
        <tbody>
          <template v-for="(group, groupIndex) in groupedStudents">
            <tr v-if="group.students.length > 0" :key="`${groupIndex}-${group.students[0].student.id}`" :class="getTypeClass(group.students[0].type)">
              <td :rowspan="group.count" class="text-center">{{ groupIndex + 1 }}</td>
              <td :rowspan="group.count" class="text-center">{{ getStudentType(group.students[0].type) }}</td>
              <td :rowspan="group.count" class="student-name" :title="group.students[0].student.full_name">
                <a :href="`${group.students[0].student.url}`">{{ truncatedName(group.students[0].student.full_name) }}</a>
              </td>
              <td :title="group.students[0].course">{{ truncatedName(group.students[0].course) }}</td>
              <td>{{ formattedNumbers(group.students[0].tuition_fee) }}</td>
              <td>{{ formattedNumbers(group.students[0].commission) }}</td>
              <td>{{ getQtyPercentage(group.students[0].qty_percentage) }}</td>
              <td>{{ getEnhancerPercentage(group.students[0].enhancers, ['key_partner', 'level']) }}</td>
              <td>{{ getEnhancerPercentage(group.students[0].enhancers, ['referer', 'hunter', 'self']) }}</td>
              <td class="commission-subtotal">
                      <span data-toggle="tooltip" data-placement="left" data-html="true" :title="getTotalTooltip(group.students[0].transactions)">
                        {{ formattedNumbers(group.students[0].total) }}
                      </span>
              </td>
            </tr>
            <!-- Additional rows within the group (if any) -->
            <tr v-for="(student, studentIndex) in group.students.slice(1)" :key="`${groupIndex}-${student.student.id}-${studentIndex}`" :class="getTypeClass(student.type)">
              <td :title="student.course">{{ truncatedName(student.course) }}</td>
              <td>{{ formattedNumbers(student.tuition_fee) }}</td>
              <td>{{ formattedNumbers(student.commission) }}</td>
              <td>{{ getQtyPercentage(student.qty_percentage) }}</td>
              <td>{{ getEnhancerPercentage(student.enhancers, ['key_partner', 'level']) }}</td>
              <td>{{ getEnhancerPercentage(student.enhancers, ['referer', 'hunter', 'self']) }}</td>
              <td class="commission-subtotal">
                <span data-toggle="tooltip" data-placement="left" data-html="true" :title="getTotalTooltip(student.transactions)">
                  {{ formattedNumbers(student.total) }}
                </span>
              </td>
            </tr>
          </template>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import { formatCurrency, truncateText } from "../../../utils/helpers";
import '../../../../sass/commissions/previous-commissions-table.scss';

export default {
  name: 'PreviousCommissionData',
  props: {
    commissionData: {
      type: Array,
      default: function() {
        return [];
      }
    }
  },
  computed: {
    groupedStudents() {
      const groups = {};
      this.commissionData.forEach((student, index) => {
        const studentId = student.student.id;
        if (!groups[studentId]) {
          groups[studentId] = {
            count: 0,
            students: [],
            originalIndex: index
          };
        }
        groups[studentId].count++;
        groups[studentId].students.push(student);
      });

      // Sort each group of students by their original sequence
      Object.values(groups).forEach(group => {
        group.students.sort((a, b) => {
          const aIndex = this.commissionData.indexOf(a);
          const bIndex = this.commissionData.indexOf(b);
          return aIndex - bIndex;
        });
      });

      // Sort groups based on the type order: commission, closed, prevision
      return Object.values(groups).sort((a, b) => {
        const typeOrder = { commission: 1, closed: 2, prevision: 3 };
        const aType = a.students[0].type;
        const bType = b.students[0].type;

        // Sort by type first
        const typeComparison = typeOrder[aType] - typeOrder[bType];
        if (typeComparison !== 0) return typeComparison;

        return a.originalIndex - b.originalIndex;
      });
    }
  },
  mounted() {
    this.initializeTooltips();
  },
  methods: {
    formattedNumbers(number) {
      return formatCurrency(number);
    },
    truncatedName(name, limit = 20) {
      return truncateText(name, limit);
    },
    initializeTooltips() {
      const tooltipTemplate = `
        <div class="tooltip" role="tooltip">
          <div class="arrow"></div>
          <div class="tooltip-inner"></div>
        </div>
      `;

      $(this.$el).find('[data-toggle="tooltip"]').tooltip({
        template: tooltipTemplate,
        delay: { "show": 200, "hide": 100 },
      });
    },
    getQtyPercentage(student_qty) {
      return student_qty ? `${student_qty}%` : '-';
    },
    getEnhancerPercentage(enhancers, types) {
      const enhancer = enhancers.find(enh => types.includes(enh.type));
      if (enhancer) {
        if(enhancer.type === 'level') {
          return `${enhancer.percentage}% (${enhancer.level})`;
        }else {
          return `${enhancer.percentage}% (${enhancer.type})`
        }
      }else {
        return '-';
      }
    },
    getTotalTooltip(transactions) {
      if (!transactions || transactions.length === 0) {
        return 'No transactions recorded';
      }

      let commissionTransactions = transactions.map(transaction => `
        <div class="row row-body">
          <div class="col-md-6">
            ${transaction.type}
          </div>
          <div class="col-md-6">
            ${transaction.amount}
          </div>
          <div class="col-md-6">
            ${transaction.processed_at}
          </div>
        </div>
      `).join('');

      return `
        <div class="tooltip-content total">
          <div class="row row-header">
            <div class="col-md-6 border-bottom">
              Type
            </div>
            <div class="col-md-6 border-bottom">
              Amount
            </div>
            <div class="col-md-6 border-bottom">
              Processed at
            </div>
          </div>
          ${commissionTransactions}
        </div>
      `;
    },
    getStudentType(type) {
      switch (type) {
        case 'commission':
          return 'COE/LOA';
        case 'closed':
          return 'No COE/LOA';
        default:
          return 'Prevision';
      }
    },
    getTypeClass(type) {
      switch (type) {
        case 'commission':
          return 'has-coe';
        case 'closed':
          return 'has-no-coe';
        default:
          return '';
      }
    }
  }
}
</script>